<script setup lang="ts">
import type { Message } from '@/utils/zulip/types';

import { sanitizeHtml } from '@/utils/html';

export interface Props {
  messages: Message[];
}
const props = withDefaults(defineProps<Props>(), {
  messages: () => [],
});

const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleString();
};
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
</script>

<template>
  <v-list
    :class="$style.helpcenterSideBarChatMessagesVList"
    data-testid="helpcenterSideBarChatMessagesVList"
  >
    <v-list-item
      v-for="msg in props.messages"
      :key="msg.timestamp"
      :class="$style.message"
    >
      <template #prepend>
        <v-avatar color="primary" class="text-white">
          {{ msg.sender.charAt(0) }}
        </v-avatar>
      </template>

      <v-list-item-title>{{ msg.sender }}</v-list-item-title>
      <v-list-item-subtitle>
        {{ formatDate(msg.timestamp) }}
      </v-list-item-subtitle>

      <!-- eslint-disable vue/no-v-html -->
      <div
        :class="$style.messageContent"
        class="mt-2"
        v-html="sanitizeHtml(msg.content)"
      />
      <!-- eslint-enable vue/no-v-html -->
      <!-- Display attachments if any -->
      <div
        v-if="msg.attachments && msg.attachments.length > 0"
        :class="$style.attachments"
        class="mt-2"
      >
        <v-chip
          v-for="file in msg.attachments"
          :key="file.name"
          class="ma-1"
          color="primary"
          variant="outlined"
          :href="file.url"
          target="_blank"
        >
          <v-icon start icon="mdi-paperclip"></v-icon>
          {{ file.name }} ({{ formatFileSize(file.size) }})
        </v-chip>
      </div>
    </v-list-item>
  </v-list>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.helpcenterSideBarChatMessagesVList {
}

  .messages {
    flex-grow: 1;
    overflow-y: auto;
  }

  .message {
    margin-bottom: 8px;
  }

  .attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .messageContent {
    margin: 0.5rem 0;
    word-break: break-word;
    font-size: 0.875rem;
  }

  .messageContent :global(blockquote) {
    margin: 0.5rem 0;
    padding: 0.5rem 0.75rem;
    border-left: 3px solid colors.$color-brand-800;
    background: colors.$color-gray-100;
    color: colors.$color-border-input-hovered;
  }

  .messageContent :global(.user-mention) {
    color: colors.$color-brand-500;
    font-weight: 500;
  }

  .messageContent :global(a) {
    color: colors.$color-brand-500;
    text-decoration: none;
    word-break: break-all;
  }

  .messageContent :global(a:hover) {
    text-decoration: underline;
  }
</style>
